import {
  HostAppSettings,
  HostAppSettingName,
} from '../backend/office-js/HostAppSettings';
import { IsUnlimitedFileUploadMinutesAvailableForTenant } from './SupportedUnlimitedFileUploadMinutesTenants';

export function wait(milliseconds: number): Promise<void> {
    return new Promise<void>((resolve, _) => {
        setTimeout(() => {
            resolve();
        }, milliseconds);
    });
}

export function isEmptyString(str : string){
  return isNullOrUndefined(str) || str.length == 0;
}

export function isNullOrUndefined(object: any): boolean {
  return object === undefined || object === null || !object ;
}

export function ErrorStringFromException(e: any): string {
  let exceptionString: string = "Exception: " + JSON.stringify(e);
  const stringifyException = e.toString();
  const index = stringifyException.indexOf("[object ");

  if (index == -1 && e != null) {
      if (e.name)
          exceptionString += ", name: " + e.name;
      if (e.code)
          exceptionString += ", code: " + e.code;
      if (e.message)
          exceptionString += ", message: " + e.message;
      if (e.stack)
          exceptionString += ", stack: " + e.stack;
  } else if (index != -1 && e != null) {
    try {
      if (e.name)
          exceptionString += ", name: " + e.name;
      if (e.code)
          exceptionString += ", code: " + e.code;

      let moreDiagnostics: string =
      e?.message && e?.stack
        ? `${e.message}: ${e.stack}`
        : e?.stack
        ? e.stack
        : e?.message
        ? e.message
        : JSON.stringify(e);

      return moreDiagnostics ? exceptionString + " " + moreDiagnostics : exceptionString;

      } catch (err:any) {
        const error: string = `stringifyException error: ${err.message}: ${err.stack}`;
        return exceptionString + " " + error;
      }
  }

  return exceptionString;
}

export function PromiseWithTimeout<T>(promise: () => Promise<T>, timeout : number, promiseName : string) {
    return Promise.race([
      promise(),
      wait(timeout).then(() => {
        return Promise.reject("The Promise: "+ promiseName +" timed out");
      }),
    ]);
}

export async function PromiseWithRetry<T>(promise: (attempt: number) => Promise<T>, maxRetryAllowed: number, delayInterval: number): Promise<T> {
  let lastError: any;
  for (let attempt = 0 ; attempt < maxRetryAllowed; attempt++) {
    try {
      if (attempt > 0) {
        await wait(delayInterval);
      }
      return await promise(attempt);
    } catch (e) {
      lastError = e;
    }
  }
  throw lastError;
}

export function IsFileNameValidForOneDriveUpload(fileName: string): boolean {
  //Reference Documentation - https://support.microsoft.com/en-us/office/restrictions-and-limitations-in-onedrive-and-sharepoint-64883a5d-228e-48f5-b3d2-eb39e07630fa#invalidcharacters

  //Leading and trailing space in the file name is not allowed for Onedrive file upload.
  const leadingTrailingSpaceRegex = /^\s+|\s+$/g;
  const isLeadingOrTrailingSpaceInFileName = leadingTrailingSpaceRegex.test(fileName);
  if (isLeadingOrTrailingSpaceInFileName) {
    return false;
  }

  // forbidden characters \ / : * ? " < > | in the file name for Onedrive file upload
  const fileNameValidityRegex = /^[^\\/:\*\?"<>\|]+$/;
  return fileNameValidityRegex.test(fileName);
}

/**
 * To check whether unlimited file upload limit is available for current user
 * @returns true - For all consumer licensed users, For enterprise users if tenants is listed in IsUnlimitedFileUploadMinutesAvailableForTenant
 * false : For all other cases
 */
export function IsUnlimitedFileUploadMinutesAvailableForUser(): boolean {
  const isUnlimitedMinutesEnabled: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.UnlimitedMins);
  const isLicensedConsumerUser: boolean = HostAppSettings.IsSettingTrue(HostAppSettingName.IsLicensedUser);
  const tenantId: string = HostAppSettings.GetSettingAsString(HostAppSettingName.UserTenantId);

  if (isUnlimitedMinutesEnabled && (isLicensedConsumerUser || IsUnlimitedFileUploadMinutesAvailableForTenant(tenantId))) {
    return true;
  }

  return false;
}